/**
 * Strict, shared contract for the dashboard.policy_intelligence widget.
 *
 * Widget specs are AI-authored JSON. Keep their free-form params at the UI
 * boundary until they have passed this allowlist so unknown values cannot
 * leak into a request URL, query cache key, or user-visible description.
 */

import { normalizePolicyIntelligenceBillRef } from "./policy-intelligence-identifiers.js";

export const DASHBOARD_POLICY_PRIORITY_TIERS = [
  "urgent",
  "high",
  "normal",
  "background",
] as const;

export const DASHBOARD_POLICY_STATUSES = [
  "new",
  "reviewed",
  "actioned",
  "background",
  "archived",
] as const;

export type DashboardPolicyPriorityTier = (typeof DASHBOARD_POLICY_PRIORITY_TIERS)[number];
export type DashboardPolicyStatus = (typeof DASHBOARD_POLICY_STATUSES)[number];
export type DashboardPolicySort = "priority" | "recent";

export interface DashboardPolicyIntelligenceLens {
  all_time: boolean;
  since_hours: number;
  sort: DashboardPolicySort;
  priority_tiers: DashboardPolicyPriorityTier[];
  statuses: DashboardPolicyStatus[];
  needs_review: boolean | null;
  category: string | null;
  member_bioguide: string | null;
  contact_id: string | null;
  account_id: string | null;
  issue_id: string | null;
  bill_ref: string | null;
  limit: number;
}

const DEFAULT_SINCE_HOURS = 168;
const MAX_SINCE_HOURS = 24 * 366;
const DEFAULT_LIMIT = 5;
const MAX_DASHBOARD_LIMIT = 20;
const DEFAULT_PRIORITIES: DashboardPolicyPriorityTier[] = ["urgent", "high"];
const DEFAULT_STATUSES: DashboardPolicyStatus[] = ["new", "reviewed", "actioned", "background"];
const UNSAFE_DISPLAY_CHARACTERS = /[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const BIOGUIDE_PATTERN = /^[A-Z][0-9]{6}$/;
const DASHBOARD_POLICY_INTELLIGENCE_QUERY_KEYS = [
  "all_time",
  "since_hours",
  "sort",
  "priority",
  "status",
  "needs_review",
  "category",
  "member_bioguide",
  "contact_id",
  "account_id",
  "issue_id",
  "bill_ref",
  "limit",
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(record: Record<string, unknown>, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function boundedInteger(value: unknown, fallback: number, min: number, max: number): number {
  const candidate = typeof value === "number"
    ? value
    : typeof value === "string" && /^\d+$/.test(value.trim())
      ? Number(value.trim())
      : Number.NaN;
  if (!Number.isFinite(candidate)) return fallback;
  return Math.max(min, Math.min(max, Math.trunc(candidate)));
}

function safeText(value: unknown, maxLength: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > maxLength || UNSAFE_DISPLAY_CHARACTERS.test(trimmed)) {
    return null;
  }
  return trimmed;
}

function normalizeEnumList<T extends string>(
  value: unknown,
  allowed: readonly T[],
  fallback: readonly T[],
): T[] {
  const candidates = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : null;
  if (!candidates) return [...fallback];

  const selected = new Set(
    candidates
      .filter((candidate): candidate is string => typeof candidate === "string")
      .map((candidate) => candidate.trim().toLowerCase())
      .filter((candidate): candidate is T => allowed.includes(candidate as T)),
  );
  if (selected.size === 0) return [...fallback];
  return allowed.filter((candidate) => selected.has(candidate));
}

function hasAllowedEnumValue<T extends string>(value: unknown, allowed: readonly T[]): boolean {
  const candidates = Array.isArray(value)
    ? value
    : typeof value === "string"
      ? value.split(",")
      : [];
  return candidates.some((candidate) =>
    typeof candidate === "string" && allowed.includes(candidate.trim().toLowerCase() as T)
  );
}

function normalizeNeedsReview(value: unknown, fallback: boolean | null): boolean | null {
  if (value === null) return null;
  if (value === true || value === "true" || value === "1") return true;
  if (value === false || value === "false" || value === "0") return false;
  return fallback;
}

/**
 * Normalize only the documented widget params. Missing or malformed values
 * resolve to a narrow, useful default rather than accidentally broadening the
 * dashboard query. To intentionally select every value, callers provide the
 * complete allowlisted enum rather than an empty array.
 */
export function normalizeDashboardPolicyIntelligenceLens(
  params: unknown,
): DashboardPolicyIntelligenceLens {
  const record = isRecord(params) ? params : {};
  const allTime = normalizeNeedsReview(record.all_time, false) === true;
  const member = safeText(record.member_bioguide, 40)?.toUpperCase() ?? null;
  const contact = safeText(record.contact_id, 36);
  const account = safeText(record.account_id, 36);
  const issue = safeText(record.issue_id, 36);
  const hasExplicitStatuses = hasOwn(record, "statuses")
    && hasAllowedEnumValue(record.statuses, DASHBOARD_POLICY_STATUSES);

  return {
    all_time: allTime,
    // Keep this canonical even though the server ignores it for all-time
    // lenses, so cache keys and copied links round-trip deterministically.
    since_hours: allTime
      ? DEFAULT_SINCE_HOURS
      : boundedInteger(record.since_hours, DEFAULT_SINCE_HOURS, 1, MAX_SINCE_HOURS),
    sort: record.sort === "recent" ? "recent" : "priority",
    priority_tiers: hasOwn(record, "priority_tiers")
      ? normalizeEnumList(record.priority_tiers, DASHBOARD_POLICY_PRIORITY_TIERS, DEFAULT_PRIORITIES)
      : [...DEFAULT_PRIORITIES],
    statuses: hasOwn(record, "statuses")
      ? normalizeEnumList(record.statuses, DASHBOARD_POLICY_STATUSES, DEFAULT_STATUSES)
      : [...DEFAULT_STATUSES],
    // Existing AI-authored widgets that intentionally supplied statuses keep
    // their workflow-only meaning. The new default is the actionable queue:
    // every active status whose evidence still needs acknowledgement.
    needs_review: hasOwn(record, "needs_review")
      ? normalizeNeedsReview(record.needs_review, true)
      : hasExplicitStatuses
        ? null
        : true,
    category: safeText(record.category, 200),
    member_bioguide: member && BIOGUIDE_PATTERN.test(member) ? member : null,
    contact_id: contact && UUID_PATTERN.test(contact) ? contact.toLowerCase() : null,
    account_id: account && UUID_PATTERN.test(account) ? account.toLowerCase() : null,
    issue_id: issue && UUID_PATTERN.test(issue) ? issue.toLowerCase() : null,
    bill_ref: normalizePolicyIntelligenceBillRef(record.bill_ref),
    limit: boundedInteger(record.limit, DEFAULT_LIMIT, 1, MAX_DASHBOARD_LIMIT),
  };
}

/** Stable, complete cache-key projection of a normalized lens. */
export function dashboardPolicyIntelligenceLensKey(
  lens: DashboardPolicyIntelligenceLens,
): readonly [boolean, number, DashboardPolicySort, string, string, string, string, string, string, string, string, string, number] {
  return [
    lens.all_time,
    lens.since_hours,
    lens.sort,
    lens.priority_tiers.join(","),
    lens.statuses.join(","),
    lens.needs_review === null ? "all" : String(lens.needs_review),
    lens.category ?? "",
    lens.member_bioguide ?? "",
    lens.contact_id ?? "",
    lens.account_id ?? "",
    lens.issue_id ?? "",
    lens.bill_ref ?? "",
    lens.limit,
  ];
}

/** Translate widget vocabulary to the existing REST query vocabulary. */
export function dashboardPolicyIntelligenceSearchParams(
  lens: DashboardPolicyIntelligenceLens,
): URLSearchParams {
  const params = new URLSearchParams();
  if (lens.all_time) params.set("all_time", "true");
  else params.set("since_hours", String(lens.since_hours));
  if (lens.sort !== "priority") params.set("sort", lens.sort);
  if (lens.priority_tiers.length > 0) params.set("priority", lens.priority_tiers.join(","));
  if (lens.statuses.length > 0) params.set("status", lens.statuses.join(","));
  if (lens.needs_review !== null) params.set("needs_review", String(lens.needs_review));
  if (lens.category) params.set("category", lens.category);
  if (lens.member_bioguide) params.set("member_bioguide", lens.member_bioguide);
  if (lens.contact_id) params.set("contact_id", lens.contact_id);
  if (lens.account_id) params.set("account_id", lens.account_id);
  if (lens.issue_id) params.set("issue_id", lens.issue_id);
  if (lens.bill_ref) params.set("bill_ref", lens.bill_ref);
  params.set("limit", String(lens.limit));
  return params;
}

function queryEnumValues(params: URLSearchParams, key: "priority" | "status"): string[] {
  return params.getAll(key).flatMap((value) => value.split(","));
}

/**
 * Parse only links emitted by the dashboard lens contract. A URL with none of
 * the allowlisted fields is ordinary Intake navigation and returns null.
 * Unknown query fields are ignored and never enter the normalized lens.
 */
export function parseDashboardPolicyIntelligenceLensSearch(
  search: string | URLSearchParams,
): DashboardPolicyIntelligenceLens | null {
  const params = typeof search === "string"
    ? new URLSearchParams(search.startsWith("?") ? search.slice(1) : search)
    : search;
  if (!DASHBOARD_POLICY_INTELLIGENCE_QUERY_KEYS.some((key) => params.has(key))) {
    return null;
  }
  const input: Record<string, unknown> = {
    all_time: params.get("all_time"),
    since_hours: params.get("since_hours"),
    sort: params.get("sort"),
    category: params.get("category"),
    member_bioguide: params.get("member_bioguide"),
    contact_id: params.get("contact_id"),
    account_id: params.get("account_id"),
    issue_id: params.get("issue_id"),
    bill_ref: params.get("bill_ref"),
    limit: params.get("limit"),
  };
  if (params.has("priority")) input.priority_tiers = queryEnumValues(params, "priority");
  if (params.has("status")) input.statuses = queryEnumValues(params, "status");
  if (params.has("needs_review")) input.needs_review = params.get("needs_review");
  return normalizeDashboardPolicyIntelligenceLens(input);
}

/** Remove the dashboard lens while preserving unrelated Intake query state. */
export function clearDashboardPolicyIntelligenceLensSearch(search: string): string {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  for (const key of DASHBOARD_POLICY_INTELLIGENCE_QUERY_KEYS) params.delete(key);
  params.delete("focus_story");
  const remaining = params.toString();
  return remaining ? `?${remaining}` : "";
}

function timeWindowLabel(hours: number): string {
  if (hours % (24 * 7) === 0) {
    const weeks = hours / (24 * 7);
    return `Past ${weeks} week${weeks === 1 ? "" : "s"}`;
  }
  if (hours % 24 === 0) {
    const days = hours / 24;
    return `Past ${days} day${days === 1 ? "" : "s"}`;
  }
  return `Past ${hours} hour${hours === 1 ? "" : "s"}`;
}

/** Human-readable statement of every effective lens field. */
export function describeDashboardPolicyIntelligenceLens(
  lens: DashboardPolicyIntelligenceLens,
): string {
  const parts = [
    lens.all_time ? "All-time history" : timeWindowLabel(lens.since_hours),
    lens.sort === "recent" ? "newest evidence first" : "highest priority first",
    lens.priority_tiers.length > 0 ? lens.priority_tiers.join(" + ") : "all priorities",
    lens.statuses.length > 0 ? lens.statuses.join(" + ") : "active statuses",
    lens.needs_review === true
      ? "needs review"
      : lens.needs_review === false
        ? "evidence acknowledged"
        : null,
    lens.category ? `category: ${lens.category}` : null,
    lens.member_bioguide ? `member: ${lens.member_bioguide}` : null,
    lens.contact_id ? `contact: ${lens.contact_id}` : null,
    lens.account_id ? `organization: ${lens.account_id}` : null,
    lens.issue_id ? `issue: ${lens.issue_id}` : null,
    lens.bill_ref ? `bill: ${lens.bill_ref}` : null,
    `up to ${lens.limit}`,
  ].filter((part): part is string => part !== null);
  return parts.join(" · ");
}

/** The archive link keeps the complete normalized lens inspectable/shareable. */
export function dashboardPolicyIntelligenceIntakeHref(
  lens: DashboardPolicyIntelligenceLens,
): string {
  return `/intake?${dashboardPolicyIntelligenceSearchParams(lens).toString()}#policy-intelligence-heading`;
}

export const DASHBOARD_POLICY_INTELLIGENCE_WIDGET_PARAMS_HELP = [
  "dashboard.policy_intelligence params are strictly allowlisted:",
  "all_time boolean (true overrides since_hours); sort priority|recent;",
  `since_hours integer 1-${MAX_SINCE_HOURS};`,
  `priority_tiers non-empty array of ${DASHBOARD_POLICY_PRIORITY_TIERS.join("|")};`,
  `statuses non-empty array of ${DASHBOARD_POLICY_STATUSES.join("|")};`,
  "needs_review boolean; true selects unacknowledged new or updated evidence, false selects acknowledged evidence;",
  "category string (max 200); member_bioguide in Bioguide form such as S000033;",
  "contact_id, account_id, and issue_id UUID; bill_ref string (max 100); limit integer 1-20.",
  "Defaults are 168 hours, highest priority first, urgent+high, active statuses, needs_review=true, and 5 stories. An explicit statuses list without needs_review keeps its workflow-only meaning. Unknown keys and malformed values are not sent to the API.",
].join(" ");
